import React, { useEffect, useState } from 'react';
import { format, isPast, isToday } from 'date-fns';
import { it } from 'date-fns/locale';
import { CheckCircle2, Circle, Clock, AlertCircle } from 'lucide-react';
import { EventReminder } from '../../types/client-activity.types';
import { activityService } from '../../lib/activityService';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { cn } from '../../lib/cn';
import { useStore } from '../../store/useStore';

interface ReminderListProps {
    clientId: string;
}

export function ReminderList({ clientId }: ReminderListProps) {
    const [reminders, setReminders] = useState<EventReminder[]>([]);
    const [loading, setLoading] = useState(true);
    const { addNotification } = useStore();

    const loadReminders = async () => {
        try {
            const data = await activityService.getRemindersByClient(clientId);
            setReminders(data);
        } catch (error) {
            console.error('Error loading reminders:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReminders();
    }, [clientId]);

    const handleComplete = async (id: string) => {
        try {
            await activityService.updateReminder(id, { is_completed: true });
            addNotification({ type: 'success', title: 'Reminder completato' });
            loadReminders(); // Refresh list
        } catch (error) {
            console.error(error);
            addNotification({ type: 'error', title: 'Errore', message: 'Impossibile completare il reminder' });
        }
    };

    if (loading) return <div className="text-sm text-gray-500">Caricamento reminder...</div>;
    if (reminders.length === 0) return null; // Don't show anything if no reminders

    return (
        <Card className="p-3 sm:p-4 bg-yellow-50/50 border-yellow-200 mb-4 md:mb-6">
            <h3 className="font-semibold text-yellow-800 flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4" />
                Reminder Attivi
            </h3>
            <div className="space-y-2">
                {reminders.map((reminder) => {
                    const date = new Date(reminder.remind_at);
                    const isExpired = isPast(date) && !isToday(date);
                    const isForToday = isToday(date);

                    return (
                        <div key={reminder.id} className="flex items-center justify-between gap-2 bg-white p-2 rounded border shadow-sm">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 sm:h-6 sm:w-6 flex-shrink-0 p-0 text-gray-400 hover:text-green-600"
                                    onClick={() => handleComplete(reminder.id)}
                                >
                                    <Circle className="w-4 h-4" />
                                </Button>

                                <div className="flex flex-col min-w-0">
                                    <div className="text-sm font-medium break-words">
                                        {(reminder as any).event?.title || 'Attività senza titolo'}
                                        {/* Note: activityService joins 'event', need to ensure type reflects it if we use it */}
                                    </div>
                                    <div className={cn("text-xs flex items-center gap-1",
                                        isExpired ? "text-red-600 font-bold" :
                                            isForToday ? "text-orange-600 font-bold" : "text-gray-500"
                                    )}>
                                        {isExpired && <AlertCircle className="w-3 h-3" />}
                                        {format(date, "d MMM yyyy, HH:mm", { locale: it })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}
